import { Fragment, useMemo, useState, type ChangeEvent } from 'react';
import { Box, IconButton, Text } from '@nimbus-ds/components';
import { TrashIcon } from '@nimbus-ds/icons';
import { DataList } from '@nimbus-ds/patterns';
import { InputNumberNimbus } from 'commons/components';
import { HiddenVariantTag } from 'domains/Catalog/Categories/components';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { ApplyValuesToAllVariants } from 'domains/Catalog/Products/pages/components';
import {
  extractTransferProduct,
  TransferProductVariant,
} from 'domains/Catalog/Transfers/utils';
import TransferProductCell from '../../../components/TransferProductCell';

import './TransferList.scss';

interface TransferListProps {
  productVariants: TransferProductVariant[];
  showZeroQuantityAsWarning: boolean;
  onTransferQuantityChange: (
    productId: string,
    variantId: string,
    quantity: number,
  ) => void;
  onRemoveProduct: (productId: string, variantId: string) => void;
  onApplyValuesToAllVariants: (variantId: string) => void;
  onTransferTotalStock: (productId: string) => void;
}

function TransferList({
  productVariants,
  showZeroQuantityAsWarning,
  onTransferQuantityChange,
  onRemoveProduct,
  onApplyValuesToAllVariants,
  onTransferTotalStock,
}: Readonly<TransferListProps>) {
  const t = useTranslationCatalog();

  const [variantChangedId, setVariantChangedId] = useState<
    string | undefined
  >();

  const products = useMemo(
    () => extractTransferProduct(productVariants),
    [productVariants],
  );

  const getInputAppearance = (variant: TransferProductVariant) => {
    if ((variant.stock ?? 0) - (variant.quantity ?? 0) < 0) return 'danger';
    if (showZeroQuantityAsWarning && (variant.quantity ?? 0) === 0)
      return 'warning';
    return 'neutral';
  };

  const handleInputChange =
    (variant: TransferProductVariant) => (e: ChangeEvent<HTMLInputElement>) => {
      setVariantChangedId(variant.variantId);
      const quantity = parseInt(e.target.value, 10) || 0;
      onTransferQuantityChange(variant.productId, variant.variantId, quantity);
    };

  const handleApplyValuesChange = (variant: TransferProductVariant) => () => {
    setVariantChangedId(undefined);
    onApplyValuesToAllVariants(variant.variantId);
  };

  if (productVariants.length === 0) return null;

  return (
    <Box data-style="stratus--transfer-list">
      <DataList>
        <div className="stratus--margins-expand">
          {products.map((product) => {
            const variants = productVariants.filter(
              (v) => v.productId === product.productId,
            );
            return (
              <DataList.Row
                id={product.productId}
                key={product.productId}
                topDivider
              >
                <TransferProductCell
                  product={product}
                  onTransferTotalStock={onTransferTotalStock}
                />
                <Box
                  display="grid"
                  gridTemplateColumns="65fr 25fr 10fr"
                  gridGap="1-5"
                  marginTop={variants.length > 1 ? '5' : 'none'}
                >
                  {variants.map((variant, index) => {
                    const nextStock =
                      (variant.stock ?? 0) - (variant.quantity ?? 0);
                    const hasQuantity = (variant.quantity ?? 0) > 0;
                    const variantText = Object.values(
                      variant.variantValues,
                    ).join(' / ');
                    return (
                      <Fragment key={variant.variantId}>
                        <div style={{ gridColumn: '1/4' }}>
                          <Box display="flex" flexDirection="row" gap="2">
                            <Text color="neutral-textHigh" lineClamp={1}>
                              {variantText}
                            </Text>
                            <HiddenVariantTag
                              visible={variant.variantVisible}
                            />
                          </Box>
                        </div>
                        <Box
                          display="flex"
                          gap="2"
                          alignItems="center"
                          justifyContent="flex-end"
                        >
                          <Text
                            textDecoration={
                              hasQuantity ? 'line-through' : 'none'
                            }
                          >
                            {variant.stock}
                          </Text>
                          {hasQuantity && (
                            <Text color="neutral-textLow">{nextStock}</Text>
                          )}
                        </Box>
                        <Box>
                          <InputNumberNimbus
                            type="integer"
                            aria-label={variant.variantId}
                            value={variant.quantity ?? 0}
                            appearance={getInputAppearance(variant)}
                            onChange={handleInputChange(variant)}
                            min={0}
                          />
                          {variants.length > 1 && (
                            <ApplyValuesToAllVariants
                              value={variant.quantity}
                              valueWasManuallyChanged={
                                variantChangedId === variant.variantId
                              }
                              onChange={handleApplyValuesChange(variant)}
                            />
                          )}
                        </Box>
                        <Box display="flex" justifyContent="center">
                          <IconButton
                            source={<TrashIcon size={16} />}
                            size="2rem"
                            onClick={() =>
                              onRemoveProduct(
                                variant.productId,
                                variant.variantId,
                              )
                            }
                            aria-label={`${t(
                              'transfers.new.table.headers.exclude',
                            )} ${variant.variantId}`}
                          />
                        </Box>
                        {index !== variants.length - 1 && (
                          <div
                            style={{
                              gridColumn: '1/-1',
                              margin: '8px -16px 0 -16px',
                              height: '1px',
                              backgroundColor:
                                'var(--nimbus-colors-neutral-surfaceHighlight)',
                            }}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                </Box>
              </DataList.Row>
            );
          })}
        </div>
      </DataList>
    </Box>
  );
}

export default TransferList;
